/**
 * What Hallquill sells and what each feature costs, as plain data (PRD v3.2
 * section 2): the Free plan, the three plans by the year in US dollars, the
 * Credits each brings, Credits on their own, the Standing Lawyers
 * sponsorship, and each feature's price in Credits. Rewritten on day 35.
 *
 * Plain data and nothing else, with no imports, because the page reads it
 * directly (as @ls/shared/catalogue) and the page carries no zod. The API reads
 * the same prices from here, so what the card prints and what the server
 * charges cannot drift apart. The zod that validates a tier or a firm id lives
 * in tiers.ts.
 */

/* ------------------------------------------------------------------ */
/* The plans                                                           */
/* ------------------------------------------------------------------ */

export const TIER_IDS = ["base", "premium", "elite"] as const;
export type TierId = (typeof TIER_IDS)[number];

/**
 * Daily exchange rate peg: 1 USD = 85.00 INR (8,500 paise per dollar).
 * Converted at Order Summary / checkout into INR paise for payment processing.
 */
export const DEFAULT_USD_INR_RATE = 85.0;

export interface Tier {
  readonly id: TierId;
  readonly name: string;
  /** What a year costs in USD dollars ($10, $40, $100). */
  readonly yearDollars: number;
  /** What a year costs in USD cents ($10 = 1000). */
  readonly yearCents: number;
  /** What a year costs in paise based on daily FX rate (85.0 INR/USD). */
  readonly yearPaise: number;
}

/**
 * Base: $10 / year (7 Credits)
 * Premium: $40 / year (50 Credits)
 * Elite: $100 / year (90 Credits)
 * Standing Lawyers Sponsorship: $88 discount on Elite (Net payable $12 / year)
 * On-Demand Credit Top-Up: $1.50 per Credit
 * (PRD v3.2, 18 October 2026)
 */
export const TIERS: readonly Tier[] = [
  {
    id: "base",
    name: "Base",
    yearDollars: 10,
    yearCents: 1_000,
    yearPaise: 85_000,
  },
  {
    id: "premium",
    name: "Premium",
    yearDollars: 40,
    yearCents: 4_000,
    yearPaise: 340_000,
  },
  {
    id: "elite",
    name: "Elite",
    yearDollars: 100,
    yearCents: 10_000,
    yearPaise: 850_000,
  },
];

/**
 * The plans a sponsorship voucher can be spent on: Elite alone (PRD v3.2
 * section 2, D14: "applies exclusively toward the Elite Plan").
 */
export const VOUCHER_TIERS: readonly TierId[] = ["elite"];

/** Standing Lawyers Sponsorship discount on Elite: $88 (88% off $100). */
export const STANDING_LAWYERS_SPONSORSHIP_DOLLARS = 88;
export const STANDING_LAWYERS_SPONSORSHIP_CENTS = 8_800;
export const STANDING_LAWYERS_SPONSORSHIP_PAISE = 748_000;

/** On-Demand Research Credits Top-Up pack ($1.50 per Credit). */
export const CREDIT_TOPUP_PACK = {
  unitDollars: 1.5,
  unitCents: 150,
  unitPaise: 12_750,
  packCredits: 5,
  packDollars: 7.5,
  packPaise: 63_750,
} as const;

/** Annual Credits granted with each plan (PRD v3.2 Section 2). */
export const TIER_CREDITS: Readonly<Record<TierId, number>> = { base: 7, premium: 50, elite: 90 };

/**
 * Every account has a plan: Free until it takes another (PRD v3.2 section 2,
 * "Accounts open at once"). Free carries no Credits and keeps the free
 * features below, as every plan does.
 */
export const FREE_PLAN = { name: "Free", credits: 0 } as const;

/**
 * A Standing Lawyers intern's 30 days of Elite, pre-provisioned by email with
 * Elite's Credits (PRD v3.2 section 2). The days count from the day the admin
 * site provisions the address.
 */
export const INTERN_DAYS = 30;
export const INTERN_CREDITS = 90;

/**
 * What stays free on every account (D19, decided 22 October 2026: the PRD's
 * proposal): the writing score and grammar marks in Writing Assistant, and a
 * citation formatted from details the student enters.
 */
export const FREE_FEATURES: readonly string[] = [
  "The writing score and grammar marks in Writing Assistant",
  "Citations formatted from details you enter",
];

/**
 * What each feature costs in Credits, by its route id (PRD v3.2 section 2,
 * What a Credit buys), and how its price panel says it. Every feature reads its
 * price from here; the server charges from here too.
 *
 * Mohit, 28 October 2026: a price is shown only where a student asks for it,
 * behind the Credit mark in the feature's header, in a professional manner:
 * the amount, what it is per, and what is included. No feature says what was
 * spent or charged, anywhere. `notes` say what comes with it, never a warning.
 */
export interface FeaturePrice {
  readonly credits: number;
  /** "1 Credit", "12 Credits", "Up to 6 Credits". */
  readonly amount: string;
  /** "per search", "per review", "for the review". */
  readonly per: string;
  readonly notes: readonly string[];
}

export const FEATURE_PRICES: Readonly<Record<string, FeaturePrice>> = {
  "corporate-review": {
    credits: 12,
    amount: "12 Credits",
    per: "per review",
    notes: ["Read by a lawyer at a top tier firm.", "Returned within 3 working days."],
  },
  advice: {
    credits: 12,
    amount: "12 Credits",
    per: "per request",
    notes: ["Up to 12 questions in one request.", "Each further question, 1 Credit."],
  },
  "paper-review": {
    credits: 12,
    amount: "12 Credits",
    per: "per review",
    notes: ["Papers up to 5,000 words.", "Returned as tracked changes with a short letter."],
  },
  knowledge: {
    credits: 1,
    amount: "1 Credit",
    per: "per 10 searches",
    notes: ["Related works count as a search.", "Reading a paper, filters, sorting, further pages and Cite are included."],
  },
  writing: {
    credits: 1,
    amount: "1 Credit",
    per: "per paper",
    notes: ["Papers up to 5,000 words.", "The writing score and grammar marks are included on every plan."],
  },
  plagiarism: { credits: 1, amount: "1 Credit", per: "per report", notes: ["Papers up to 5,000 words."] },
  "ai-detection": { credits: 1, amount: "1 Credit", per: "per report", notes: ["Papers up to 5,000 words."] },
  citation: {
    credits: 1,
    amount: "1 Credit",
    per: "per paper",
    notes: ["Every citation a paper needs, found and written in your style.", "A citation from details you enter is included on every plan."],
  },
  courses: { credits: 6, amount: "Up to 6 Credits", per: "per course", notes: ["Set by each course's depth, and shown on the course."] },
  publications: {
    credits: 12,
    amount: "12 Credits",
    per: "for the review",
    notes: ["Submitting a paper is included.", "The review is only for a paper the editors select."],
  },
};

/**
 * Knowledge Base's searches to a Credit (Mohit, 28 October 2026; D20 was 1
 * Credit a search). The search that opens a pack spends the Credit, and the
 * next nine come from the pack. A search that fails or finds nothing takes no
 * place in a pack and opens none.
 */
export const SEARCHES_PER_CREDIT = 10;

/**
 * The writing score (day 44) and the grammar marks (day 38) in Writing
 * Assistant: included on every plan, as D19 decided. Both run in the student's
 * browser, from rules alone, so no Credit is held; the page says so in these
 * words.
 */
export const MARKS_PRICE = { credits: 0, words: "The writing score and grammar marks are included on every plan" } as const;

export function tierOf(id: TierId): Tier {
  const tier = TIERS.find((each) => each.id === id);
  if (!tier) throw new Error(`no tier ${id}`);
  return tier;
}

/** Converts USD dollars to INR paise using daily FX rate. */
export function usdToPaise(dollars: number, fxRate = DEFAULT_USD_INR_RATE): number {
  return Math.round(dollars * fxRate * 100);
}

/** Formats dollar amount as currency string, e.g. "$10", "$1.50". */
export function formatDollars(dollars: number): string {
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/* ------------------------------------------------------------------ */
/* Law firms, for the Standing Lawyers Internship Portal                */
/* ------------------------------------------------------------------ */

export interface LawFirm {
  readonly id: string;
  readonly name: string;
  /** Other names students search by: initials, former names. */
  readonly aka?: readonly string[];
}

/** For a firm not in the list. Always offered, last. */
export const OTHER_FIRM_ID = "other";

/**
 * India's top tier law firms, and Standing Lawyers, in alphabetical order.
 * Names exactly as The Legal 500 Asia Pacific 2026 ranks them (Corporate and
 * M&A, Banking and finance, Dispute resolution: litigation, and Intellectual
 * property), checked 29 September 2026. Recent renames are in aka so the old
 * name still finds the firm: J. Sagar Associates became JSA Advocates &
 * Solicitors; L&L Partners went back to Luthra and Luthra Law Offices India
 * (April 2026); IndusLaw joined CMS as CMS INDUSLAW; Obhan & Associates merged
 * with Mason & Associates as Obhan Mason.
 *
 * An id is stored against the redemption, so an id never changes once shipped;
 * a firm that renames keeps its id and gains an aka.
 */
export const LAW_FIRMS: readonly LawFirm[] = [
  { id: "almt-legal", name: "ALMT Legal" },
  { id: "anand-and-anand", name: "Anand and Anand" },
  { id: "argus-partners", name: "Argus Partners" },
  { id: "azb", name: "AZB & Partners", aka: ["AZB"] },
  { id: "bharucha", name: "Bharucha & Partners" },
  { id: "induslaw", name: "CMS INDUSLAW", aka: ["IndusLaw", "Indus Law"] },
  { id: "cam", name: "Cyril Amarchand Mangaldas", aka: ["CAM"] },
  { id: "dentons-link-legal", name: "Dentons Link Legal", aka: ["Link Legal"] },
  { id: "desai-diwanji", name: "Desai & Diwanji" },
  { id: "dhir-and-dhir", name: "Dhir & Dhir Associates" },
  { id: "dsk-legal", name: "DSK Legal" },
  { id: "dua-associates", name: "Dua Associates" },
  { id: "elp", name: "Economic Laws Practice", aka: ["ELP"] },
  { id: "fox-mandal", name: "Fox Mandal & Associates" },
  { id: "hsa", name: "HSA Advocates", aka: ["HSA"] },
  { id: "jsa", name: "JSA Advocates & Solicitors", aka: ["JSA", "J. Sagar Associates"] },
  { id: "juris-corp", name: "Juris Corp" },
  { id: "karanjawala", name: "Karanjawala & Company" },
  { id: "keystone-partners", name: "Keystone Partners" },
  { id: "khaitan", name: "Khaitan & Co LLP", aka: ["Khaitan & Co", "KCO"] },
  { id: "king-stubb-kasiva", name: "King, Stubb & Kasiva", aka: ["KSK"] },
  { id: "kochhar", name: "Kochhar & Co." },
  { id: "lakshmikumaran-sridharan", name: "Lakshmikumaran & Sridharan", aka: ["L&S", "LKS"] },
  { id: "ll-partners", name: "Luthra and Luthra Law Offices India", aka: ["Luthra & Luthra", "L&L Partners"] },
  { id: "majmudar", name: "Majmudar & Partners" },
  { id: "nishith-desai", name: "Nishith Desai Associates", aka: ["NDA"] },
  { id: "obhan", name: "Obhan Mason", aka: ["Obhan & Associates"] },
  { id: "phoenix-legal", name: "Phoenix Legal" },
  { id: "poovayya", name: "Poovayya & Co" },
  { id: "remfry-sagar", name: "Remfry & Sagar" },
  { id: "s-and-r", name: "S&R Associates", aka: ["S and R"] },
  { id: "samvad-partners", name: "Samvad Partners" },
  { id: "saraf-partners", name: "Saraf and Partners" },
  { id: "sam", name: "Shardul Amarchand Mangaldas & Co", aka: ["SAM"] },
  { id: "singhania", name: "Singhania & Partners LLP" },
  { id: "sng-partners", name: "SNG & Partners" },
  { id: "spice-route-legal", name: "Spice Route Legal" },
  { id: "standing-lawyers", name: "Standing Lawyers" },
  { id: "tt-and-a", name: "Talwar Thakore & Associates", aka: ["TT&A", "TTA"] },
  { id: "trilegal", name: "Trilegal" },
  { id: "tuli-and-co", name: "Tuli & Co" },
  { id: "vaish-associates", name: "Vaish Associates" },
  { id: "veritas-legal", name: "Veritas Legal" },
  { id: "wadia-ghandy", name: "Wadia Ghandy & Co" },
  { id: OTHER_FIRM_ID, name: "Another firm" },
];

export const LAW_FIRM_IDS: readonly string[] = LAW_FIRMS.map((firm) => firm.id);

/** Lower case, "&" read as "and", punctuation gone, so "l and l" finds L&L. */
function fold(text: string): string {
  return text
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * The firms a search matches, best first: a name or aka that is the query
 * (so "SAM" is Shardul's before it is Samvad's), then one that starts with
 * it, then one with a word that does, then one that contains it anywhere.
 * An empty query is the whole list. "Another firm" always stays at the end.
 */
export function searchFirms(query: string): readonly LawFirm[] {
  const wanted = fold(query);
  if (!wanted) return LAW_FIRMS;
  const scored: { firm: LawFirm; score: number }[] = [];
  for (const firm of LAW_FIRMS) {
    if (firm.id === OTHER_FIRM_ID) continue;
    let best = 0;
    for (const name of [firm.name, ...(firm.aka ?? [])]) {
      const folded = fold(name);
      if (folded === wanted) best = Math.max(best, 4);
      else if (folded.startsWith(wanted)) best = Math.max(best, 3);
      else if (folded.split(" ").some((word) => word.startsWith(wanted))) best = Math.max(best, 2);
      else if (folded.replace(/ /g, "").includes(wanted.replace(/ /g, ""))) best = Math.max(best, 1);
    }
    if (best > 0) scored.push({ firm, score: best });
  }
  scored.sort((a, b) => b.score - a.score || a.firm.name.localeCompare(b.firm.name));
  const other = LAW_FIRMS.find((firm) => firm.id === OTHER_FIRM_ID);
  return other ? [...scored.map((each) => each.firm), other] : scored.map((each) => each.firm);
}

/* ------------------------------------------------------------------ */
/* The student's programme and year                                    */
/* ------------------------------------------------------------------ */

export const PROGRAMME_IDS = ["5_year", "3_year"] as const;
export type ProgrammeId = (typeof PROGRAMME_IDS)[number];

/**
 * The LLB programmes a student can be on, and how many years each runs
 * (Mohit, 1 October 2026: the product was for first to third year only). The
 * year a student picks cannot be past their programme's last.
 */
export const PROGRAMMES: readonly { readonly id: ProgrammeId; readonly label: string; readonly years: number }[] = [
  { id: "5_year", label: "5-year LLB", years: 5 },
  { id: "3_year", label: "3-year LLB", years: 3 },
];

/** Year of study as its stored digit, and the words on the form. */
export const YEAR_LABELS: Readonly<Record<string, string>> = {
  "1": "1st year",
  "2": "2nd year",
  "3": "3rd year",
  "4": "4th year",
  "5": "5th year",
};

/** The years a programme has, as stored digits: ["1","2","3"] for the 3-year LLB. */
export function yearsOf(programme: ProgrammeId): readonly string[] {
  const found = PROGRAMMES.find((each) => each.id === programme);
  return Array.from({ length: found?.years ?? 0 }, (_, i) => String(i + 1));
}

/**
 * Whether a year fits a programme. A student from before programmes existed
 * has none recorded, and keeps the three years the product began with until
 * they say which they are on.
 */
export function yearFits(programme: ProgrammeId | null, year: string): boolean {
  return (programme === null ? ["1", "2", "3"] : yearsOf(programme)).includes(year);
}
